import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, Plus, Calendar, Clock, ArrowRight, Eye } from 'lucide-react';
import api from '../services/api';
import { RetrospectiveSession } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { RecentRetrospectivesSkeleton } from './SkeletonLoaders';
import EmptyState from './EmptyState';

interface RecentRetrospectivesProps {
  limit?: number;
}

const RecentRetrospectives: React.FC<RecentRetrospectivesProps> = ({ limit = 5 }) => {
  const [retrospectives, setRetrospectives] = useState<RetrospectiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecentRetrospectives = async () => {
      // Check if token exists before making request
      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        setError('Please log in to view retrospectives');
        return;
      }

      try {
        setLoading(true);
        // Fetch all retrospectives and sort by most recent
        // Backend wraps PaginatedResponse in WriteSuccess, so response is { data: { data: [...], meta: {...} } }
        const response = await api.get<{ data: { data: RetrospectiveSession[]; meta?: any } }>('/retrospectives');
        const paginatedData = response.data.data;
        const data = Array.isArray(paginatedData?.data) ? paginatedData.data : [];
        const sorted = data
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, limit);
        setRetrospectives(sorted);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch retrospectives:', err);
        setError(err.response?.data?.error?.message || 'Failed to load retrospectives');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentRetrospectives();
  }, [limit]);

  const handleRetrospectiveClick = (retroId: string) => {
    navigate(`/retrospectives/${retroId}`);
  };

  const getFormatLabel = (format: string): string => {
    const formatMap: Record<string, string> = {
      'start-stop-continue': 'Start/Stop/Continue',
      'mad-sad-glad': 'Mad/Sad/Glad',
      'well-didnt-improve': 'Well/Didn\'t/Improve',
      '4ls': '4Ls (Liked/Learned/Lacked/Longed)',
      'daki': 'DAKI',
      'sailboat': 'Sailboat',
      'rose-thorn-bud': 'Rose/Thorn/Bud',
    };
    return formatMap[format] || format;
  };

  if (loading) {
    return <RecentRetrospectivesSkeleton />;
  }

  if (error) {
    return (
      <EmptyState
        emoji="⚠️"
        title="Failed to load retrospectives"
        description={error}
        action={{
          label: 'Retry',
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  if (retrospectives.length === 0) {
    return (
      <EmptyState
        icon={History}
        title="No retrospectives yet"
        description="Select a team and create your first retrospective"
        action={{
          label: 'View Teams',
          onClick: () => navigate('/dashboard'),
          icon: Plus,
        }}
      />
    );
  }

  return (
    <Card className="shadow-sm border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5 text-primary" />
            Recent Retros
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/retrospectives')}
            className="h-8 text-sm gap-1"
          >
            View All <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          {retrospectives.map((retro, index) => (
            <div
              key={retro.id}
              onClick={() => handleRetrospectiveClick(retro.id)}
              role="button"
              tabIndex={0}
              style={{ animationDelay: `${index * 50}ms` }}
              className={cn(
                "group relative rounded-none border-2 border-[#161E54] bg-white p-4 transition-all hover:shadow-neo hover:-translate-y-1 cursor-pointer animate-fade-in"
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRetrospectiveClick(retro.id);
                }
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                        {retro.teamName && (
                            <div className="text-xs font-bold text-[#161E54]/70 uppercase tracking-wide">
                                {retro.teamName}
                            </div>
                        )}
                        <h3 className="text-base font-semibold text-gray-900 leading-tight tracking-tight">
                        {retro.title}
                        </h3>
                    </div>
                    <Badge
                      variant={retro.status === 'active' ? 'default' : 'secondary'}
                      className={cn(
                        "text-xs uppercase tracking-wide flex-shrink-0",
                        retro.status === 'active' && "bg-success-100 text-success-700 hover:bg-success-100 border-success-300",
                        retro.status === 'ended' && "bg-gray-200 text-gray-700 hover:bg-gray-200 border-gray-300"
                      )}
                    >
                      {retro.status}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                    <span className="font-medium">{getFormatLabel(retro.format)}</span>
                    {retro.isAnonymous && (
                      <Badge
                        variant="secondary"
                        className="bg-warning-100 text-warning-700 hover:bg-warning-100 border-warning-300 text-xs"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Anonymous
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{retro.startTime ? new Date(retro.startTime).toLocaleDateString() : 'Not started'}</span>
                    <Clock className="h-3 w-3 ml-2" />
                    <span>{retro.startTime ? new Date(retro.startTime).toLocaleTimeString() : '-'}</span>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform flex-shrink-0 mt-1" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentRetrospectives;
